import React from "react";
import produce from "immer";
import { useDispatch } from "react-redux";
import {
  Alert,
  Box,
  Checkbox,
  Divider,
  IconButton,
  InputAdornment,
  Typography
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import styles from "../style.module.css";
import { tl } from "../../../components/translate";
import { ResourceCentreActionsType } from "../../../actions";
import { SettingsType } from "../Settings";
import DebouncedTextField from "../../../components/DebouncedTextField";
import useCan from "../../../hooks/useCan";
import {
  OkhatiAiSettings as OkhatiAiSettingsType,
  ResourceCentre
} from "../../../interfaces/ResourceCentreInterface";
import { mergeResourceCentreOkhatiAiSettings } from "../../../hooks/useOkhatiAiSettings";

interface Props {
  state: SettingsType;
  setState: React.Dispatch<React.SetStateAction<SettingsType>>;
  actions: ResourceCentreActionsType;
}

const defaultOkhatiAiSettings = (): OkhatiAiSettingsType => ({
  enabled: false,
  transcriptionApiKey: "",
  prompt: ""
});

const OkhatiAiSettings = ({ state, setState, actions }: Props): React.ReactNode => {
  const dispatch = useDispatch();
  const isSuperAdmin = useCan("deny", { superAdminPass: true });
  const [showApiKey, setShowApiKey] = React.useState(false);

  const okhatiAiSettings = {
    ...defaultOkhatiAiSettings(),
    ...state.settings.okhatiAiSettings
  };

  const isEnabled = okhatiAiSettings.enabled ?? false;

  const updateOkhatiAiSettings = (updates: Partial<OkhatiAiSettingsType>) => {
    const newState = produce(state, (draft) => {
      draft.settings.okhatiAiSettings = {
        ...defaultOkhatiAiSettings(),
        ...draft.settings.okhatiAiSettings,
        ...updates
      };
    });

    setState(newState);
    actions.putResourceCentre(newState, (responseRc: ResourceCentre) => {
      const merged = mergeResourceCentreOkhatiAiSettings(newState, responseRc);
      dispatch({ type: "GET_RESOURCECENTRE", payload: merged });
      dispatch({ type: "UPDATE_USER_RC", payload: merged });
    });
  };

  return (
    <Box>
      <Box sx={{ p: 2 }}>
        <Typography component="h3" sx={{ fontWeight: "bold" }}>
          Okhati AI
        </Typography>
      </Box>

      <Divider />

      {!isSuperAdmin && (
        <Box p={2}>
          <Alert severity="info">{tl("resourceCentre.okhatiAiSuperAdminOnly")}</Alert>
          <Box mt={2}>
            <Typography variant="body2" color="text.secondary">
              {tl("resourceCentre.okhatiAiStatus")}:{" "}
              <strong>{isEnabled ? tl("enabled") : tl("disabled")}</strong>
            </Typography>
          </Box>
        </Box>
      )}

      {isSuperAdmin && (
        <Box ml="10px" p={2} display="flex" flexDirection="column" gap={2}>
          <Box display="flex" alignItems="center">
            <Checkbox
              className={styles.optPadding}
              size="small"
              checked={isEnabled}
              onChange={() => {
                updateOkhatiAiSettings({ enabled: !isEnabled });
              }}
            />
            <Typography>{tl("resourceCentre.enableOkhatiAiForResourceCentre")}</Typography>
          </Box>

          <Box display="flex" alignItems="center">
            <Typography sx={{ minWidth: "180px" }}>
              {tl("resourceCentre.transcriptionApiKey")}
            </Typography>
            <DebouncedTextField
              disabled={!isEnabled}
              type={showApiKey ? "text" : "password"}
              variant="outlined"
              label={tl("resourceCentre.transcriptionApiKey")}
              slotProps={{
                inputLabel: { shrink: true },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showApiKey ? "Hide API key" : "Show API key"}
                        onClick={() => setShowApiKey((prev) => !prev)}
                        edge="end"
                        size="small"
                        disabled={!isEnabled}
                      >
                        {showApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }
              }}
              style={{ minWidth: "400px" }}
              value={okhatiAiSettings.transcriptionApiKey || ""}
              debounceAt={1000}
              onChange={(e) => {
                updateOkhatiAiSettings({ transcriptionApiKey: e.target.value });
              }}
            />
          </Box>

          <Typography component="h4" sx={{ fontWeight: "bold", mt: 1 }}>
            {tl("resourceCentre.opdSummary")}
          </Typography>

          <Box display="flex" alignItems="flex-start" gap={2}>
            <Typography sx={{ minWidth: "180px", pt: 1 }}>
              {tl("resourceCentre.okhatiAiPrompt")}
            </Typography>
            <DebouncedTextField
              disabled={!isEnabled}
              variant="outlined"
              label={tl("resourceCentre.okhatiAiPrompt")}
              placeholder={tl("resourceCentre.okhatiAiPromptPlaceholder")}
              multiline
              minRows={6}
              maxRows={12}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              style={{ minWidth: "400px" }}
              value={okhatiAiSettings.prompt || ""}
              debounceAt={1000}
              onChange={(e) => {
                updateOkhatiAiSettings({ prompt: e.target.value });
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default OkhatiAiSettings;
